"use client";

import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Navigation, Thumbs } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import Image from "next/image";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/thumbs";

interface Photo {
  url: string;
  title?: string;
}

export default function TourGallerySlider({ photos }: { photos: Photo[] }) {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  if (!photos.length) return null;

  const safeUrl = (url: string) => url.replace(/ /g, "%20");
  const hasMultiple = photos.length > 1;

  return (
    <div
      className="slider-area tour-slider1 mb-4"
      style={{ position: "relative" }}
    >
      {/* ── Main Slider ── */}
      <Swiper
        className="th-slider mb-3"
        modules={[EffectFade, Navigation, Thumbs]}
        effect="fade"
        loop={hasMultiple}
        thumbs={{
          swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
        }}
        navigation={{
          prevEl: ".tour-slider-prev",
          nextEl: ".tour-slider-next",
        }}
      >
        {photos.map((photo, i) => (
          <SwiperSlide key={i}>
            <div className="tour-slider-img" style={{ position: "relative" }}>
              {photo.title && <p className="text">{photo.title}</p>}
              <Image
                src={safeUrl(photo.url)}
                alt={photo.title || "Tour photo"}
                width={800}
                height={500}
                style={{ width: "100%", height: "450px", objectFit: "cover" }}
                unoptimized
                priority={i === 0}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* ── Thumbnail Slider ── */}
      {hasMultiple && (
        <Swiper
          className="th-slider tour-thumb-slider"
          modules={[Thumbs]}
          onSwiper={setThumbsSwiper}
          loop={photos.length > 3}
          watchSlidesProgress
          spaceBetween={8}
          breakpoints={{
            0: { slidesPerView: 2 },
            576: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            992: { slidesPerView: 3 },
            1200: { slidesPerView: 3 },
          }}
        >
          {photos.map((photo, i) => (
            <SwiperSlide key={i} style={{ cursor: "pointer" }}>
              <div className="tour-slider-img">
                <Image
                  src={safeUrl(photo.url)}
                  alt={photo.title || ""}
                  width={300}
                  height={180}
                  style={{ width: "100%", height: "180px", objectFit: "cover" }}
                  unoptimized
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* ── Navigation Arrows ── */}
      {hasMultiple && (
        <>
          <button className="slider-arrow style3 slider-next tour-slider-next">
            <Image
              src="/main/img/icon/hero-arrow-right.svg"
              alt="Next"
              width={24}
              height={24}
            />
          </button>
          <button className="slider-arrow style3 slider-prev tour-slider-prev">
            <Image
              src="/main/img/icon/hero-arrow-left.svg"
              alt="Previous"
              width={24}
              height={24}
            />
          </button>
        </>
      )}
    </div>
  );
}
